import {
  draftFromCoords,
  findLatLngInText,
  osmReverseGeocode,
  osmSearchAddress,
} from './addressGeocodeUtils';

export { draftFromCoords, findLatLngInText };

export const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  process.env.REACT_APP_GOOGLE_PLACES_API_KEY ||
  process.env.REACT_APP_MAPS_API_KEY ||
  '';

export const DEFAULT_MAP_CENTER = { lat: 17.493, lng: 78.391 };

export const EMPTY_FORM = {
  id: null,
  name: '',
  address: '',
  city: '',
  state: '',
  country: 'India',
  postalCode: '',
  latitude: '',
  longitude: '',
  isPrimary: true,
  type: 0,
};

let googleMapsScriptPromise = null;

export function loadGoogleMaps() {
  if (!GOOGLE_MAPS_API_KEY) return Promise.reject(new Error('Google Maps key is not configured.'));
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (googleMapsScriptPromise) return googleMapsScriptPromise;

  googleMapsScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-digirasa-google-maps="true"]');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.google.maps), { once: true });
      existing.addEventListener('error', () => reject(new Error('Could not load Google Maps.')), {
        once: true,
      });
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      GOOGLE_MAPS_API_KEY,
    )}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.dataset.digirasaGoogleMaps = 'true';
    script.onload = () => resolve(window.google.maps);
    script.onerror = () => reject(new Error('Could not load Google Maps.'));
    document.head.appendChild(script);
  });

  return googleMapsScriptPromise;
}

const LEAFLET_VERSION = '1.9.4';
let leafletScriptPromise = null;

export function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (leafletScriptPromise) return leafletScriptPromise;

  if (!document.querySelector('link[data-digirasa-leaflet="true"]')) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.css`;
    link.dataset.digirasaLeaflet = 'true';
    document.head.appendChild(link);
  }

  leafletScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-digirasa-leaflet="true"]');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.L), { once: true });
      existing.addEventListener('error', () => reject(new Error('Could not load the map.')), {
        once: true,
      });
      return;
    }

    const script = document.createElement('script');
    script.src = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.js`;
    script.async = true;
    script.defer = true;
    script.dataset.digirasaLeaflet = 'true';
    script.onload = () => resolve(window.L);
    script.onerror = () => reject(new Error('Could not load the map.'));
    document.head.appendChild(script);
  });

  return leafletScriptPromise;
}

/**
 * Build a Leaflet marker icon backed by CDN images so bundler asset paths are not required.
 *
 * @param {typeof import('leaflet')} L
 */
export function createLeafletMarkerIcon(L) {
  return L.icon({
    iconUrl: `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/images/marker-icon.png`,
    iconRetinaUrl: `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/images/marker-icon-2x.png`,
    shadowUrl: `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/images/marker-shadow.png`,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });
}

export function cleanText(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

/**
 * @param {unknown} raw
 */
function normalizeAddressId(raw) {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function normalizeAddressFromRaw(raw) {
  const safe = raw && typeof raw === 'object' ? raw : {};
  return {
    id: normalizeAddressId(safe.id ?? safe.userAddressId ?? safe.addressId),
    userID: safe.userID ?? null,
    name: String(safe.name || ''),
    address: String(safe.address || ''),
    city: String(safe.city || ''),
    state: String(safe.state || ''),
    country: String(safe.country || ''),
    postalCode: String(safe.postalCode ?? ''),
    latitude: Number.isFinite(Number(safe.latitude)) ? Number(safe.latitude) : null,
    longitude: Number.isFinite(Number(safe.longitude)) ? Number(safe.longitude) : null,
    isPrimary: Boolean(safe.isPrimary),
    type: Number.isFinite(Number(safe.type)) ? Number(safe.type) : 3,
  };
}

function getAddressPart(result, types) {
  const components = result?.address_components || [];
  const match = components.find((component) =>
    types.some((type) => component.types?.includes(type)),
  );
  return match?.long_name || '';
}

export function geocodeResultToDraft(result) {
  const location = result?.geometry?.location;
  const lat = typeof location?.lat === 'function' ? location.lat() : location?.lat;
  const lng = typeof location?.lng === 'function' ? location.lng() : location?.lng;
  const streetNumber = getAddressPart(result, ['street_number']);
  const route = getAddressPart(result, ['route']);
  const sublocality = getAddressPart(result, [
    'sublocality_level_1',
    'sublocality',
    'neighborhood',
    'premise',
  ]);
  const addressLine = cleanText([streetNumber, route, sublocality].filter(Boolean).join(', '));

  return {
    formattedAddress: result?.formatted_address || '',
    address: addressLine || result?.formatted_address || '',
    city:
      getAddressPart(result, ['locality']) ||
      getAddressPart(result, ['administrative_area_level_3']) ||
      getAddressPart(result, ['postal_town']),
    state: getAddressPart(result, ['administrative_area_level_1']),
    country: getAddressPart(result, ['country']) || 'India',
    postalCode: getAddressPart(result, ['postal_code']),
    latitude: Number(lat),
    longitude: Number(lng),
  };
}

export function parseMapUrlInput(value) {
  const text = String(value || '').trim();
  const coords = findLatLngInText(text);
  if (coords) return { coords, query: '' };

  try {
    const url = new URL(text);
    if (/maps\.app\.goo\.gl|goo\.gl/i.test(url.hostname)) {
      return {
        coords: null,
        query: '',
        error:
          'Short Google Maps links cannot be read directly in the browser. Open the link, copy the full maps.google.com URL, or use coordinates.',
      };
    }
    const params = url.searchParams;
    const paramValue =
      params.get('q') ||
      params.get('query') ||
      params.get('destination') ||
      params.get('daddr') ||
      params.get('ll') ||
      '';
    const paramCoords = findLatLngInText(paramValue);
    if (paramCoords) return { coords: paramCoords, query: '' };
    const placeMatch = decodeURIComponent(url.pathname).match(/\/(?:maps\/)?place\/([^/]+)/i);
    const pathPlace = placeMatch ? placeMatch[1].replace(/\+/g, ' ') : '';
    return { coords: null, query: cleanText(paramValue || pathPlace || text) };
  } catch {
    return { coords: null, query: cleanText(text) };
  }
}

export function coordsToGoogleEmbedUrl(coords, zoom = 15) {
  const lat = Number(coords?.lat) || DEFAULT_MAP_CENTER.lat;
  const lng = Number(coords?.lng) || DEFAULT_MAP_CENTER.lng;
  return `https://www.google.com/maps?q=${encodeURIComponent(`${lat},${lng}`)}&z=${zoom}&output=embed`;
}

export function queryToGoogleEmbedUrl(query, zoom = 15) {
  return `https://www.google.com/maps?q=${encodeURIComponent(query)}&z=${zoom}&output=embed`;
}

/**
 * @param {object} draft
 * @param {object} previous
 * @param {number} addressesLength
 */
export function mapDraftToForm(draft, previous, addressesLength) {
  return {
    ...previous,
    name: previous.name || '',
    address: draft.address || previous.address || draft.formattedAddress || '',
    city: draft.city || previous.city || '',
    state: draft.state || previous.state || '',
    country: draft.country || previous.country || 'India',
    postalCode: draft.postalCode || previous.postalCode || '',
    latitude: Number.isFinite(draft.latitude) ? draft.latitude.toFixed(6) : previous.latitude,
    longitude: Number.isFinite(draft.longitude) ? draft.longitude.toFixed(6) : previous.longitude,
    isPrimary: previous.id == null && addressesLength === 0 ? true : previous.isPrimary,
  };
}

/**
 * @param {{ lat: number, lng: number }} coords
 * @param {import('google.maps').Geocoder | null} geocoderRef
 */
export async function reverseGeocodeCoords(coords, geocoderRef) {
  if (!GOOGLE_MAPS_API_KEY) return osmReverseGeocode(coords);
  const maps = await loadGoogleMaps();
  const geocoder = geocoderRef || new maps.Geocoder();
  const response = await geocoder.geocode({ location: coords });
  const result = response.results?.[0];
  return result ? geocodeResultToDraft(result) : draftFromCoords(coords);
}

/**
 * @param {string} text
 * @param {import('google.maps').Geocoder | null} geocoderRef
 */
export async function geocodeTextQuery(text, geocoderRef) {
  if (!GOOGLE_MAPS_API_KEY) {
    const coords = findLatLngInText(text);
    if (coords) return draftFromCoords(coords);
    return osmSearchAddress(text);
  }
  const maps = await loadGoogleMaps();
  const geocoder = geocoderRef || new maps.Geocoder();
  const response = await geocoder.geocode({
    address: text,
    componentRestrictions: { country: 'IN' },
  });
  const result = response.results?.[0];
  if (!result) throw new Error('No matching Google location was found.');
  return geocodeResultToDraft(result);
}
